import { generateDynamicThemeToken } from '../../ThemeProvider';
import { getDynamicThemeToken } from './theme';
export const zoomIn = {
    '@keyframes ui-from-zoom-in': {
        '0%': {
            transform: 'scale(0.2)',
            opacity: 0,
        },
        '100%': {
            transform: 'scale(1)',
            opacity: 1,
        },
    },
};
const componentName = 'Form';
const prefixName = `ui-${componentName}`;
const itemClass = `${prefixName}-item`;
const labelClass = `${itemClass}-label`;
export const formClasses = {
    root: prefixName,
    large: `${prefixName}-large`,
    text: `${prefixName}-text`,
    item: {
        root: itemClass,
        explain: `${itemClass}-explain`,
        explainError: `${itemClass}-explain-error`,
        explainWarning: `${itemClass}-explain-warning`,
        extra: `${itemClass}-extra`,
        extraWithExplain: `${itemClass}-extra-with-explain`,
        withHelp: `${itemClass}-with-help`,
        hidden: `${itemClass}-hidden`,
        hasError: `${itemClass}-has-error`,
    },
    label: {
        root: `${labelClass}-root`,
        container: `${labelClass}-container`,
        label: `${labelClass}-label`,
        wrapper: `${labelClass}-wrapper`,
        outerWrapper: `${labelClass}-outerWrapper`,
        innerWrapper: `${labelClass}-innerWrapper`,
        left: `${labelClass}-left`,
        right: `${labelClass}-right`,
        wrap: `${labelClass}-wrap`,
        required: `${labelClass}-required`,
        optional: `${labelClass}-optional`,
        tooltipWrapper: `${labelClass}-tooltipWrapper`,
        tooltip: `${labelClass}-tooltip`,
        colon: `${labelClass}-colon`,
        requiredMark: `${labelClass}-requiredMark`,
        optionalMark: `${labelClass}-optionalMark`,
        description: `${labelClass}-desc`,
    },
    layout: {
        vertical: `${prefixName}-vertical`,
        horizontal: `${prefixName}-horizontal`,
        inline: `${prefixName}-inline`,
    },
    control: {
        root: `${itemClass}-control`,
        input: `${itemClass}-control-input`,
        content: `${itemClass}-control-input-content`,
    },
};
export const resetComponent = (token) => ({
    boxSizing: 'border-box',
    margin: 0,
    padding: 0,
    color: token.color.text,
    fontSize: token.fontSize.default,
    listStyle: 'none',
    fontFamily: token.fontFamily,
});
const resetForm = (token) => ({
    legend: {
        display: 'block',
        width: '100%',
        marginBottom: token.margin.large,
        padding: 0,
        color: token.color.secondary,
        fontSize: token.fontSize.default,
        lineHeight: 'inherit',
        border: 0,
        borderBottom: `${token.border.width}px ${token.border.type} ${token.border.color.default}`,
    },
    label: {
        fontSize: token.fontSize.default,
    },
    'input[type="search"]': {
        boxSizing: 'border-box',
    },
    // Position radios and checkboxes better
    'input[type="radio"], input[type="checkbox"]': {
        lineHeight: 'normal',
    },
    'input[type="file"]': {
        display: 'block',
    },
    // Make range inputs behave like textual form controls
    'input[type="range"]': {
        display: 'block',
        width: '100%',
    },
    // Make multiple select elements height not fixed
    'select[multiple], select[size]': {
        height: 'auto',
    },
    // Focus for file, radio, and checkbox
    [`input[type='file']:focus,
  input[type='radio']:focus,
  input[type='checkbox']:focus`]: {
        outline: 0,
    },
    // Adjust output element
    output: {
        display: 'block',
        paddingTop: 15,
        color: token.color.text,
        fontSize: token.fontSize.default,
        lineHeight: token.lineHeight.default,
    },
});
const genFormSize = (height) => ({
    [`.${formClasses.item.root}`]: {
        [`.${formClasses.item.root}-label > label`]: {
            height,
        },
        [`.${formClasses.item.root}-control-input`]: {
            minHeight: height,
        },
    },
});
const genFormStyle = (token) => ({
    [`&.${formClasses.root}`]: {
        ...resetComponent(token),
        ...resetForm(token),
        [`.${formClasses.root}-text`]: {
            display: 'inline-block',
            paddingInlineEnd: token.padding,
        },
        [`.${formClasses.large}`]: {
            ...genFormSize(token.control.height.large),
        },
    },
});
const genFormItemStyle = (token) => 
// const iconCls = '';
({
    [`.${formClasses.item.root}`]: {
        ...resetComponent(token),
        marginBottom: token.margin.default,
        verticalAlign: 'top',
        [`&.${formClasses.item.withHelp}`]: {
            transition: 'none',
        },
        [`&.${formClasses.item.hidden},&.${formClasses.item.hidden}.${formClasses.root}-grid`]: {
            display: 'none',
        },
        // ==============================================================
        // =                            Label                           =
        // ==============================================================
        [`.${formClasses.label.root}`]: {
            [`.${formClasses.label.container}`]: {
                display: 'flex',
            },
            [`.${formClasses.label.wrapper}`]: {
                display: 'inline-flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: '4px',
                [`@media (max-width: ${token.screen.max.sm}px)`]: {
                    justifyContent: 'flex-start',
                },
                [`.${formClasses.label.outerWrapper}`]: {
                    [`.${formClasses.label.innerWrapper}`]: {
                        display: 'inline-block',
                        verticalAlign: 'top',
                        [`.${formClasses.label.tooltipWrapper}`]: {
                            display: 'inline-block',
                            verticalAlign: 'inherit',
                            fontSize: 0,
                        },
                    },
                },
                [`.${formClasses.label.label}`]: {
                    color: token.label.color,
                    fontSize: token.fontSize.default,
                    lineHeight: '20px',
                    wordWrap: 'break-word',
                    wordBreak: 'break-word',
                    verticalAlign: 'inherit',
                },
                [`.${formClasses.label.requiredMark}`]: {
                    color: token.color.error,
                    height: '20px',
                    lineHeight: '20px',
                    overflow: 'hidden',
                    flexShrink: 0,
                    marginInlineStart: '4px',
                },
                [`.${formClasses.label.optionalMark}`]: {
                    color: token.color.optional,
                },
                [`&.${formClasses.label.colon}`]: {
                    '&::after': {
                        content: '":"', // compatible with browsers that don't support content alt text
                    },
                },
                [`&.${formClasses.label.colon}::after`]: {
                    content: '":" / ""', // use content alt text to hide the colon from screen readers
                    height: '20px',
                    lineHeight: '18px',
                },
            },
            [`&.${formClasses.label.right}`]: {
                [`.${formClasses.label.outerWrapper}`]: {
                    flex: 1,
                    textAlign: 'right',
                    [`@media (max-width: ${token.screen.max.sm}px)`]: {
                        textAlign: 'unset',
                        flex: 'unset',
                    },
                },
            },
            [`&.${formClasses.label.left}`]: {
                [`.${formClasses.label.wrapper}`]: {
                    justifyContent: 'flex-start',
                },
            },
            [`&.${formClasses.label.wrap}`]: {
                overflow: 'unset',
                whiteSpace: 'unset',
            },
        },
        // ==============================================================
        // =                            Input                           =
        // ==============================================================
        [`.${formClasses.control.root}`]: {
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            [`&:first-of-type:not([class^="MuiGrid"]):not([class*="MuiGrid"])`]: {
                width: '100%',
            },
            [`.${formClasses.control.input}`]: {
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                minHeight: token.control.height.default,
                [`.${formClasses.control.content}`]: {
                    flex: 'auto',
                    maxWidth: '100%',
                },
            },
        },
    },
    // ==============================================================
    // =                           Explain                          =
    // ==============================================================
    [`.${formClasses.item.explain}, .${formClasses.item.extra}`]: {
        clear: 'both',
        color: token.color.secondary,
        fontSize: token.fontSize.small,
        transition: `color ${token.motion.duration.mid} ${token.motion.type.easeOut}`, // sync input color transition
    },
    [`.${formClasses.item.explain}-connected`]: {
        width: '100%',
    },
    [`.${formClasses.item.explainError}-container`]: {
        display: 'flex',
        flexWrap: 'nowrap',
        marginTop: 8,
        alignItems: 'center',
    },
    [`.${formClasses.item.explainError}-icon`]: {
        display: 'inline-block',
        height: 14,
        lineHeight: token.lineHeight.explain,
        color: token.color.error,
        marginInlineEnd: 4,
    },
    [`.${formClasses.item.explain}`]: {
        [`.${formClasses.item.explainError}`]: {
            color: token.color.error,
        },
        [`.${formClasses.item.explainWarning}`]: {
            color: token.color.warning,
        },
    },
    [`&.${formClasses.item.withHelp} .${formClasses.item.explain}`]: {
        height: 'auto',
        lineHeight: token.lineHeight.explain,
        opacity: 1,
    },
    [`&.${formClasses.item.hasError}`]: {
        [`> input`]: {
            borderColor: `${token.border.color.error}`,
        },
    },
    [`.${formClasses.item.extra}`]: {
        marginTop: 4,
    },
    [`.${formClasses.item.extraWithExplain}`]: {
        marginTop: 4,
    },
});
const genHorizontalStyle = () => ({
    [`&.${formClasses.layout.horizontal}`]: {
        [`.${formClasses.label.root}`]: {
            flexGrow: 0,
            alignItems: 'flex-start',
            [`.${formClasses.label.wrapper}`]: {
                width: '100%',
                minHeight: '32px',
                paddingRight: '8px',
            },
        },
        [`.${formClasses.control.root}`]: {
            flex: '1 1 0',
            minWidth: 0,
        },
    },
});
const genInlineStyle = (token) => ({
    [`&.${formClasses.layout.inline}`]: {
        display: 'flex',
        flexWrap: 'wrap',
        [`.${formClasses.item.root}`]: {
            flex: 'none',
            flexWrap: 'nowrap',
            marginInlineEnd: token.margin.default,
            marginBottom: 0,
            [`.${formClasses.label.root}`]: {
                [`.${formClasses.label.wrapper}`]: {
                    minHeight: '32px',
                    paddingRight: '8px',
                },
            },
            [`&.${formClasses.item.withHelp}`]: {
                marginBottom: token.margin.default,
            },
            [`> .${formClasses.label.root},
        > .${formClasses.control.root}`]: {
                display: 'inline-block',
                verticalAlign: 'top',
            },
            [`> .${formClasses.label.root}`]: {
                flex: 'none',
            },
            [`.${formClasses.root}-text`]: {
                display: 'inline-block',
            },
        },
    },
});
const makeVerticalLayoutLabel = (token) => ({
    margin: 0,
    padding: `0 0 ${token.padding}px`,
    whiteSpace: 'initial',
    textAlign: 'start',
});
const makeVerticalLayout = (token) => ({
    [`.${formClasses.item.root} .${formClasses.label.root}`]: {
        ...makeVerticalLayoutLabel(token),
    },
    [`&.${formClasses.root}`]: {
        [`.${formClasses.item.root}`]: {
            flexWrap: 'wrap',
            [`.${formClasses.label.root},
          .${formClasses.control.root}`]: {
                flex: '0 0 100%',
                maxWidth: '100%',
            },
        },
    },
});
const genVerticalStyle = (token) => ({
    [`&.${formClasses.root}-vertical`]: {
        [`.${formClasses.item.root}`]: {
            [`.${formClasses.item.root}-grid`]: {
                flexDirection: 'column',
            },
            [`.${formClasses.label.root}`]: {
                [`.${formClasses.label.container}`]: {
                    justifyContent: 'space-between',
                },
                [`.${formClasses.label.wrapper}`]: {
                    justifyContent: 'flex-start',
                    paddingBottom: '4px',
                    width: 'auto',
                    height: 'auto',
                    lineHeight: '20px',
                },
            },
            [`.${formClasses.label.description}`]: {
                paddingBottom: '4px',
                ...token.label.description,
            },
            [`.${formClasses.control.root}`]: {
                width: '100%',
            },
        },
    },
    [`&.${formClasses.root}-vertical .${formClasses.item.root}-label`]: {
        ...makeVerticalLayoutLabel(token),
    },
    [`@media (max-width: ${token.screen.max.sm}px)`]: {
        ...makeVerticalLayout(token),
    },
});
// ============================== Export ==============================
export function getStyle(theme) {
    const dynamicToken = getDynamicThemeToken(theme);
    const [token] = generateDynamicThemeToken({
        componentName: 'Form',
        theme,
        dynamicToken,
    });
    return {
        ...genFormStyle(token),
        ...genFormItemStyle(token),
        ...genHorizontalStyle(),
        ...genInlineStyle(token),
        ...genVerticalStyle(token),
    };
}
